import React from "react";
import { useTranslation } from "react-i18next";
import Card from "./Card";
import IconButton from "../components/IconButton";
import DeleteIcon from "../../icons/DeleteIcon";
import SettingIcon from "../../icons/SettingIcon";

type ChartCardProps = {
  title: string | React.ReactNode;
  subtitle?: string | React.ReactNode;
  left?: React.ReactNode;
  right?: React.ReactNode;
  searchPlaceholder?: string;
  onSearchChange?: (value: string) => void;
  actions?: Array<{
    label: string;
    content?: React.ReactNode;
    onClick?: (event) => void;
  }>;
  children?: React.ReactNode;
  className?: string;
  isLoading?: boolean;
  isEmpty?: boolean;
  onSetting?: (event) => void;
  onDelete?: (event) => void;
};

const ChartCard = ({
  title,
  subtitle,
  left,
  right,
  searchPlaceholder,
  onSearchChange,
  actions = [],
  children,
  className = "",
  isLoading = false,
  isEmpty = false,
  onSetting,
  onDelete,
}: ChartCardProps) => {
  const { t } = useTranslation();

  return (
    <Card className={"p-5 group " + className}>
      <div className="flex items-start justify-between">
        <div className="w-1/2">
          {isLoading ? (
            <>
              <div className="h-5 w-40 bg-gray-100 rounded animate-pulse" />
              {subtitle !== undefined && (
                <div className="h-4 w-32 bg-gray-100 rounded mt-2 animate-pulse" />
              )}
            </>
          ) : (
            <>
              <div className="text-gray-800 font-bold text-base">{title}</div>
              {subtitle && <div className="text-gray-500 text-sm mt-0.5">{subtitle}</div>}
            </>
          )}
        </div>
        <div className="flex gap-2 items-center opacity-0 group-hover:opacity-100 transition-opacity">
          {searchPlaceholder && (
            <div className="hidden md:flex items-center gap-2 border border-gray-200 rounded-lg h-10 px-3">
              <span className="text-gray-500">🔍</span>
              <input
                className="outline-none text-sm placeholder:text-gray-400 w-40"
                placeholder={searchPlaceholder}
                onChange={(e) => onSearchChange && onSearchChange(e.target.value)}
              />
            </div>
          )}
          {left}
          {actions.map((a, idx) => (
            <IconButton
              key={idx}
              label={a.label}
              className="cursor-pointer"
              onClick={(event) => a.onClick(event)}
            >
              {a.content || <span>⋯</span>}
            </IconButton>
          ))}
          <IconButton
            label={t("Settings")}
            className="cursor-pointer"
            onClick={(event) => onSetting && onSetting(event)}
          >
            <SettingIcon />
          </IconButton>
          <IconButton
            label={t("Delete")}
            className="cursor-pointer"
            onClick={(event) => onDelete && onDelete(event)}
          >
            <DeleteIcon />
          </IconButton>
          {/* <IconButton label={t("Move")}>
            <MoveIcon />
          </IconButton> */}
          {right}
        </div>
      </div>
      <div className="mt-4">
        {isLoading ? (
          <div className="space-y-3">
            <div className="animate-pulse h-6 bg-gray-100 rounded" />
            <div className="animate-pulse h-56 bg-gray-100 rounded" />
          </div>
        ) : isEmpty ? (
          <div className="h-56 border border-dashed border-gray-200 rounded flex items-center justify-center text-gray-500">
            No data
          </div>
        ) : (
          children
        )}
      </div>
    </Card>
  );
};

export default ChartCard;
